import { ApiSchema } from "@api-types";
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
} from "rxjs";
import { getAuthService } from "./auth.service";
import { odataFuncList, odataFuncOne } from "./odataApi";
import { getClientService } from "./client.service";

class UserService {
  private _user$ = new BehaviorSubject<Pick<
    ApiSchema.User,
    | "id"
    | "roleId"
    | "active"
    | "firstName"
    | "lastName"
    | "displayName"
    | "email"
    | "cognitoUsers"
    | "isIdeallyAdmin"
    | "role"
    | "initials"
    | "hubspotRoleId"
    | "researchInterpretationConfidence"
    | "researchSetupConfidence"
    | "createdAt"
    | "hubspotRole"
  > | null>(null);
  public user$ = this._user$.asObservable();
  private _hmac$ = new BehaviorSubject<string | null | undefined>(undefined);
  public hmac$ = this._hmac$.asObservable();

  public permission$ = getClientService().defaultClientId$.pipe(
    filter((c) => !!c),
    switchMap((clientId) => odataFuncList("funcUsersMyPermissions", {}, { clientId: clientId }).$()),
    map((a) => a.value),
    shareReplay(1),
  );

  public policies$ = getClientService().defaultClientId$.pipe(
    filter((c) => !!c),
    switchMap((clientId) => odataFuncList("funcUsersMyPolicies", {}, { clientId: clientId }).$()),
    map((a) => a.value),
    shareReplay(1),
  );

  constructor() {
    // Fetch user whenever auth state changes (login, token refresh, etc.)
    getAuthService()
      .state$.pipe(filter((state) => !!state))
      .subscribe(() => this.fetchUser());

    this._user$
      .pipe(
        filter((u) => !!u),
        map((u) => u.id),
        distinctUntilChanged(),
        switchMap(() => odataFuncOne("funcUsersHmacMe", {}, { noClientId: true }).$()),
        catchError(() => {
          getAuthService().logout();
          return of(null);
        }),
      )
      .subscribe((v) => this._hmac$.next(v));
  }

  public fetchUser = () => {
    const authService = getAuthService();
    authService.getTokens().subscribe((tokens) => {
      if (!tokens) {
        authService.redirectToLogin();
        return;
      }
    });
    const q = odataFuncOne("funcUsersMe", {}, { noClientId: true })
      .expandAndSelect("role", null, "id", "roleName")
      .expandAndSelect("cognitoUsers", null, "cognitoIdp", "cognitoIdpId", "id", "username")
      .expandAndSelect("hubspotRole", null, "id", "roleLabel");
    q.$().subscribe((user) => this._user$.next(user));
  };

  public getOnboardingRedirectPath = (
    user: Pick<
      ApiSchema.User,
      | "firstName"
      | "lastName"
      | "hubspotRoleId"
      | "researchInterpretationConfidence"
      | "researchSetupConfidence"
    > | null,
  ): string | undefined => {
    if (
      !user?.firstName ||
      !user?.lastName ||
      !user.hubspotRoleId ||
      !user?.researchInterpretationConfidence ||
      !user?.researchSetupConfidence
    ) {
      return "/onboarding/check-in";
    }

    const last_url = localStorage?.getItem && localStorage.getItem("last_url");
    localStorage.removeItem("last_url");

    return last_url?.startsWith("/auth") ? undefined : (last_url ?? undefined);
  };
}

let userService: UserService | null = null;

export const getUserService = () => {
  if (!userService) {
    userService = new UserService();
  }
  return userService;
};
